import { useState, useEffect, useCallback } from "react";
import { getLiveVehicles, MOCK_DEPARTURES } from "../utils/bods";

/**
 * Poll live vehicle positions every 15 seconds. These are SIRI-VM vehicle
 * positions from BODS, not stop departures.
 */
export function useLiveVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const doFetch = useCallback(async () => {
    try {
      const data = await getLiveVehicles();
      setVehicles(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError("Live vehicles unavailable");
      setVehicles([]);
    }
  }, []);

  useEffect(() => {
    let interval = null;
    const timer = setTimeout(() => {
      doFetch();
      interval = setInterval(doFetch, 15000);
    }, 500);

    return () => {
      clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [doFetch]);

  return { vehicles, error, lastUpdated };
}

async function fetchStopServices(atco) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    // Use the prebuilt GTFS index endpoint (fast, no per-request BODS zip scan).
    // This is the correct data source for the departure board per architecture notes.
    const res = await fetch(`/api/gtfs-board?atco=${encodeURIComponent(atco)}`, { signal: controller.signal });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || data.detail || `GTFS board failed: ${res.status}`);
    return data;
  } finally {
    clearTimeout(timer);
  }
}

function fmtDue(minutes, time) {
  // Clean passenger-facing Due text. Never show technical strings.
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) return time || "Scheduled";
  if (minutes <= 1) return "Due";
  if (minutes < 60) return `${minutes} mins`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

function serviceRowsToBoardRows(services) {
  return services.map((service, index) => {
    const first = service.nextTimes?.[0] || service.scheduledTimes?.[0] || {};
    const laterTimes = (service.nextTimes || []).slice(1, 3).map((t) => t.time);
    return {
      route: service.route,
      dest: service.destination || "See timetable",
      via: "",
      operatorName: service.operatorName || "",
      nextTimes: service.nextTimes || [],
      time: first.time || "Scheduled",
      mins: typeof first.minutes === "number" ? first.minutes : null,
      dueText: fmtDue(first.minutes, first.time),
      laterTimes,
      serviceOnly: true,
      serviceType: service.serviceType || "Normal",
      key: `${service.route}-${service.destination}-${service.operatorName || index}`,
      destinations: [service.destination].filter(Boolean)
    };
  });
}

/**
 * Gets exact scheduled departures for a stop using the prebuilt GTFS index.
 * Due comes from GTFS stop_times for the exact stop — never guessed from bus GPS.
 */
export function useDepartures(stop) {
  const [departures, setDepartures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState("none");
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const stopId = typeof stop === "string" ? stop : stop?.id;

  useEffect(() => {
    let cancelled = false;
    if (!stopId) return undefined;

    setLoading(true);
    setError(null);
    setMeta(null);

    fetchStopServices(stopId)
      .then((data) => {
        if (cancelled) return;
        setMeta(data.meta || null);
        if (data.services?.length) {
          setDepartures(serviceRowsToBoardRows(data.services));
          setSource("gtfs-index");
        } else {
          const localRows = MOCK_DEPARTURES[stopId] || [];
          setDepartures(localRows.map((r) => ({ ...r, dueText: r.dueText || r.time })));
          setSource(localRows.length ? "verified-demo" : "none");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const localRows = MOCK_DEPARTURES[stopId] || [];
        setDepartures(localRows);
        setSource(localRows.length ? "verified-demo" : "none");
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [stopId]);

  return { departures, loading, source, error, meta };
}
